/**
 * Top-banner slot — lets the active page dock content (the workflow Dynamic-Island
 * stepper, per BO UI Standard §5) into the shared shell's banner zone, directly
 * under the top bar and above the page content.  AppShell holds the banner state,
 * renders it in the slot, and provides `setBanner` to descendant pages.
 *
 *   const { setBanner } = useBanner();
 *   useEffect(() => { setBanner(<WorkflowStepper … />); return () => setBanner(null); }, [deps]);
 */
import { createContext, useContext, type ReactNode } from 'react';

export interface BannerApi {
  setBanner: (node: ReactNode | null) => void;
  /** Dock page-scoped content into the shared top bar's right cluster
   *  (e.g. MDL's compliance / signing badges), left of the brand block. */
  setTopbarRight: (node: ReactNode | null) => void;
  /** Dock page-scoped content CENTERED in the shared top bar — the Class C/D
   *  Dynamic-Island stepper (BO UI Standard §5). Balanced by equal flex:1 side
   *  groups so it sits at the bar's true midpoint. */
  setTopbarCenter: (node: ReactNode | null) => void;
}

export const BannerContext = createContext<BannerApi>({ setBanner: () => {}, setTopbarRight: () => {}, setTopbarCenter: () => {} });

/** Page-side hook — returns `{ setBanner, setTopbarRight }` to mount/clear the
 *  shell's top banner and the top-bar right slot. */
export const useBanner = (): BannerApi => useContext(BannerContext);
